import { toUserMessage } from '../../lib/errors.js';
// DocumentDetailPage — Vista dettaglio fattura/DDT da fiscal_archive.documents.
// Mostra: header documento, fornitore, righe (document_lines), eventuale scansione PDF/img
// con signed URL temporaneo. Risolve: gap "DocumentsPage IonItem button senza handler".

import {
  IonBackButton,
  IonBadge,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonListHeader,
  IonNote,
  IonPage,
  IonSpinner,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { documentTextOutline, openOutline, businessOutline, pricetagOutline, eyeOutline, trashOutline, swapHorizontalOutline, downloadOutline, createOutline, checkmarkCircleOutline, sparklesOutline } from 'ionicons/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useHistory } from 'react-router-dom';
import { useRef, useState } from 'react';
import { IonAlert, IonToast, IonActionSheet, IonModal, IonInput, IonTextarea } from '@ionic/react';
import { UomSelect } from '../../components/UomSelect.js';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '../../lib/adapters/share/index.js';
import { getActiveOperatorAssertion } from '../../lib/auth/activeOperatorSession.js';
import { getDeviceId } from '../../lib/db/local.js';
import { ReviewOriginalRecoveryPanel } from '../../components/ReviewOriginalRecoveryPanel.js';

// T-189: preferenza "come aprire il file originale" (ricordata).
//  - 'inapp'   → visualizzatore in-app (browser tab: rende immagini E PDF)
//  - 'external'→ condividi / apri con un'altra app (share sheet legacy)
type DocOpenMode = 'inapp' | 'external';
const DOC_OPEN_PREF_KEY = 'trackeat_doc_open_pref';
function loadDocOpenPref(): DocOpenMode {
  try {
    return localStorage.getItem(DOC_OPEN_PREF_KEY) === 'external' ? 'external' : 'inapp';
  } catch {
    return 'inapp';
  }
}

/**
 * Visualizzatore universale in-app: apre l'URL firmato in un browser tab
 * (Custom Tab su Android, SFSafariViewController su iOS, nuova scheda sul web).
 * Rende nativamente sia foto sia PDF — niente share sheet, niente download.
 */
async function openInAppViewer(url: string): Promise<void> {
  // Dynamic import (come App.tsx/AuthProvider): il plugin nativo è già nell'APK,
  // ma l'import dinamico evita problemi su eventuali APK più vecchi / sul web.
  const { Browser } = await import('@capacitor/browser');
  await Browser.open({ url, presentationStyle: 'fullscreen' });
}
import { getSupabase } from '../../lib/supabase.js';
import { useDecryptColumn } from '../../lib/crypto/useDecrypted.js';
import { useAuth } from '../../lib/auth/AuthProvider.js';
import { useEnqueueStockCommand, useEvents } from '../../lib/data/queries.js';
import { gatherReceiptData, type ReceiptInput } from '../../lib/documents/receiptData.js';
import { buildReceiptPdf } from '../../lib/documents/receiptPdf.js';
import { buildDocumentCancellationConfirmation } from '../../lib/documents/documentCancellationConfirmation.js';
import { getSyncEngine } from '../../lib/sync/engine.js';
import { stockEventMatchesBusiness } from '../../lib/stockEventBusinessScope.js';
import { formatMoney } from '../../lib/money.js';
import {
  buildStockCorrectionCommandV1,
  stockCorrectionConversionV1,
} from '@trackeat/shared';

interface DocumentBusinessScope {
  business_id: string | null;
  allow_legacy_unscoped: boolean;
}

// T-196: rettifica lotto applicata a una riga del documento (per food_id).
// T-331: la rettifica copre anche quantità + UdM del carico (qty/uom).
interface LotCorrection {
  lot: string | null;
  expiry: string | null;
  from: string | null;
  at: string;
  note: string | null;
  qty: number | null;
  uom: string | null;
  event_id: string;
  event_client_uuid: string;
  event_server_rev: number;
  command_id: string | null;
  command_seq: number | null;
  root_event_id: string | null;
  business_id: string | null;
  storage_unit_id: string | null;
  supplier_id: string | null;
  unit_cost: number | null;
  currency: string;
}

interface CorrectionIndex {
  byRootEventId: Record<string, LotCorrection>;
  legacyByFoodId: Record<string, LotCorrection>;
}

export interface CorrectionSourceSnapshot {
  food_id: string;
  lot: string | null;
  expiration_date: string | null;
  qty: number;
  uom: string;
  business_id: string;
  storage_unit_id: string | null;
  supplier_id: string | null;
  unit_cost: number | null;
  currency: string;
  document_id: string;
  original_event_id: string;
  original_event_client_uuid: string;
  original_event_server_rev: number;
  original_command_id: string | null;
  original_command_seq: number | null;
  correction_root_event_id: string;
}

// T-196: riga del documento selezionata per la correzione del lotto.
interface CorrectTarget extends CorrectionSourceSnapshot {
  food_name: string;
  canonical_uom: string;
}

/**
 * T-196: ultime rettifiche lotto di QUESTO documento, indicizzate per food_id.
 * Sono gli `stock_in` append-only con reason='lot_correction' emessi da questa
 * pagina. Servono a sovrapporre il lotto corretto alla riga del documento.
 */
async function fetchCorrections(
  sb: ReturnType<typeof getSupabase>,
  tenantId: string,
  docId: string,
  businessScope: DocumentBusinessScope,
): Promise<CorrectionIndex> {
  const { data: evs } = await sb
    .from('events')
    .select('id, client_uuid, business_id, server_rev, client_ts, payload')
    .eq('tenant_id', tenantId)
    .eq('kind', 'stock_in')
    .filter('payload->>document_id', 'eq', docId)
    .filter('payload->>reason', 'eq', 'lot_correction')
    // L'ultima correzione è quella con revisione server maggiore. client_ts è
    // controllato dal device e può essere fuori ordine dopo uso offline.
    .order('server_rev', { ascending: true });
  const out: CorrectionIndex = { byRootEventId: {}, legacyByFoodId: {} };
  for (const e of (evs ?? []) as Array<{
    id: string;
    client_uuid: string;
    business_id: string | null;
    server_rev: number;
    client_ts: string;
    payload: Record<string, unknown>;
  }>) {
    const p = e.payload;
    if (!stockEventMatchesBusiness(
      { business_id: e.business_id, payload: p },
      businessScope.business_id,
      businessScope.allow_legacy_unscoped,
    )) {
      continue;
    }
    const fid = typeof p.food_id === 'string' ? p.food_id : '';
    if (!fid) continue;
    const rootEventId = stringOrNull(p.correction_root_event_id);
    const correction: LotCorrection = {
      lot: typeof p.batch_lot === 'string' ? p.batch_lot : null,
      expiry: typeof p.expiration_date === 'string' ? p.expiration_date : null,
      from: typeof p.corrected_from_lot === 'string' ? p.corrected_from_lot : null,
      at: e.client_ts,
      note: typeof p.correction_note === 'string' ? p.correction_note : null,
      qty: numberOrNull(p.stock_qty) ?? numberOrNull(p.qty),
      uom: stringOrNull(p.stock_uom) ?? stringOrNull(p.uom),
      event_id: e.id,
      event_client_uuid: e.client_uuid,
      event_server_rev: e.server_rev,
      command_id: stringOrNull(p.command_id),
      command_seq: numberOrNull(p.command_seq),
      root_event_id: rootEventId,
      // A legacy unscoped event is attributable only because this page has
      // proved that the tenant has exactly one active site.
      business_id: businessScope.business_id,
      storage_unit_id: stringOrNull(p.storage_unit_id),
      supplier_id: stringOrNull(p.supplier_id),
      unit_cost: numberOrNull(p.stock_unit_cost) ?? numberOrNull(p.unit_cost),
      currency: (stringOrNull(p.source_currency) ?? stringOrNull(p.currency) ?? 'EUR').toUpperCase(),
    };
    if (rootEventId) out.byRootEventId[rootEventId] = correction;
    else out.legacyByFoodId[fid] = correction;
  }
  return out;
}

async function openFileNative(url: string, isPdf: boolean): Promise<void> {
  const resp = await fetch(url);
  if (!resp.ok) throw new Error(`Download fallito (${resp.status})`);
  const blob = await resp.blob();
  const base64 = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      resolve(result.split(',')[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
  const mime = blob.type.split(';', 1)[0];
  const ext = isPdf ? 'pdf' : mime === 'image/webp' ? 'webp' : mime === 'image/png' ? 'png' : 'jpg';
  const filename = `documento_${Date.now()}.${ext}`;
  const written = await Filesystem.writeFile({
    path: filename,
    data: base64,
    directory: Directory.Cache,
  });
  await Share.share({ files: [written.uri] });
}

/** Base64-encode PDF bytes in chunks (avoids call-stack overflow on big files). */
function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

/**
 * Output a generated receipt-summary PDF, platform-aware.
 *  - Native (Android/iOS): write to Cache + native Share sheet (save/share),
 *    because `<a download>` does not work inside the Capacitor WebView.
 *  - Web/PWA: blob URL + anchor download — same approach as the working
 *    "esporta registri" flow (ReportsPage). The previous code always opened
 *    the native Share sheet, which on PWA is a "condividi" screen that cannot
 *    render/open the file, so the user could never view the PDF.
 */
async function exportReceiptPdf(bytes: Uint8Array, filename: string): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    const written = await Filesystem.writeFile({
      path: filename,
      data: bytesToBase64(bytes),
      directory: Directory.Cache,
    });
    await Share.share({ files: [written.uri], title: 'Scheda riepilogo ricevimento merce' });
    return;
  }
  // Web/PWA: download via blob URL + anchor (lets the browser open/view the PDF).
  // Copy into a plain ArrayBuffer so the bytes are an unambiguous BlobPart
  // (TS strict typed-array generics reject Uint8Array<ArrayBufferLike> here).
  const buf = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(buf).set(bytes);
  const blob = new Blob([buf], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** True when the error is just the user dismissing the native share sheet. */
function isShareCancellation(err: unknown): boolean {
  const msg = (err instanceof Error ? err.message : String(err ?? '')).toLowerCase();
  return msg.includes('cancel') || msg.includes('abort') || msg.includes('dismiss');
}

interface FiscalDoc {
  id: string;
  tenant_id: string | null;
  business_id: string | null;
  archived_at?: string | null;
  type: 'fattura' | 'ddt' | 'nota_credito' | 'nota_debito' | 'autofattura';
  number: string;
  date: string;
  total_amount: number | null;
  currency: string;
  supplier_id: string | null;
  status: string;
  sdi_id: string | null;
  received_via_provider: string | null;
  pdf_file_id: string | null;
  image_file_id: string | null;
  xml_file_id: string | null;
  parsed_at: string | null;
  notes: string | null;
  created_at: string;
  // T-061: controllo conformità HACCP al ricevimento merci. Popolato solo
  // per documenti `public.documents` (DDT). Per le fatture in fiscal_archive
  // l'esito non viene mostrato (schema separato senza queste colonne).
  conformity_status?: 'conforme' | 'non_conforme' | 'pending' | null;
  conformity_notes?: string | null;
  conformity_checked_at?: string | null;
}

interface DocLine {
  doc_id: string;
  seq: number;
  description: string;
  qty: number;
  uom: string;
  unit_price: number | null;
  line_total: number | null;
  batch_lot: string | null;
  expiration_date: string | null;
  iva_rate: number | null;
  food_id?: string;
  food_name?: string;
  food_default_uom?: string;
  // Coordinate effettive del movimento: la vista mostra i valori sorgente,
  // mentre correzione import/lotto deve operare sulla giacenza derivata.
  stock_qty?: number;
  stock_uom?: string;
  supplier_sku?: string | null;
  is_frozen?: boolean;
  notify_expiration?: boolean;
  keep_current_lot?: boolean;
  stock_unit_cost?: number | null;
  conversion_factor?: number | null;
  conversion_source?: string | null;
  source_seq?: number;
  stock_event_id?: string;
  stock_event_client_uuid?: string;
  stock_event_server_rev?: number;
  stock_command_id?: string | null;
  stock_command_seq?: number | null;
  stock_business_id?: string | null;
  stock_storage_unit_id?: string | null;
  stock_supplier_id?: string | null;
  stock_batch_lot?: string | null;
  stock_expiration_date?: string | null;
}

export function documentLineDisplayNames(line: Pick<DocLine, 'description' | 'food_name'>): {
  documentName: string;
  warehouseName: string;
} {
  return {
    documentName: line.description?.trim() || 'Non disponibile',
    warehouseName: line.food_name?.trim() || 'Nessun alimento collegato',
  };
}

function recordOrNull(value: unknown): Record<string, unknown> | null {
  return value != null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function numberOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function stringOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

/**
 * Resolve the only safe site for a historical document. A document-scoped
 * business is authoritative even if the site was later archived. A legacy
 * unscoped document can be attributed only when the server proves that the
 * tenant currently has exactly one active site.
 */
export function resolveDocumentBusinessScope(
  documentBusinessId: string | null | undefined,
  activeBusinessIds: readonly string[],
  scopeLoaded: boolean,
): DocumentBusinessScope {
  const documentBusiness = stringOrNull(documentBusinessId);
  const uniqueActiveIds = [...new Set(activeBusinessIds.map(stringOrNull).filter(
    (businessId): businessId is string => businessId !== null,
  ))];
  if (documentBusiness) {
    return {
      business_id: documentBusiness,
      allow_legacy_unscoped:
        scopeLoaded
        && uniqueActiveIds.length === 1
        && uniqueActiveIds[0] === documentBusiness,
    };
  }
  if (!scopeLoaded || uniqueActiveIds.length !== 1) {
    return { business_id: null, allow_legacy_unscoped: false };
  }
  return {
    business_id: uniqueActiveIds[0],
    allow_legacy_unscoped: true,
  };
}

async function fetchDocumentBusinessScope(
  sb: ReturnType<typeof getSupabase>,
  tenantId: string | null,
  documentBusinessId: string | null,
): Promise<DocumentBusinessScope> {
  if (!tenantId) {
    return resolveDocumentBusinessScope(documentBusinessId, [], false);
  }
  // Two rows are enough to prove "exactly one"; the query cannot silently
  // misclassify a multi-site tenant because it deliberately stops only after
  // observing the second active site.
  const { data, error } = await sb
    .from('businesses')
    .select('id')
    .eq('tenant_id', tenantId)
    .is('archived_at', null)
    .order('id', { ascending: true })
    .limit(2);
  const activeBusinessIds = error
    ? []
    : ((data ?? []) as Array<{ id: string }>).map((business) => business.id);
  return resolveDocumentBusinessScope(
    documentBusinessId,
    activeBusinessIds,
    !error,
  );
}

/**
 * Operazioni, replacement ed export appartengono alla sede registrata sul
 * documento. La sede attiva è solo contesto di navigazione e non può
 * riattribuire un documento tenant-wide aperto dalla lista.
 */
export function documentBusinessIdForOperation(
  documentBusinessId: string | null | undefined,
  _activeBusinessId: string | null | undefined,
): string | null {
  return stringOrNull(documentBusinessId);
}

export interface DocumentDetailActionPolicy {
  can_mutate: boolean;
  can_reclassify: false;
}

/**
 * Archived/rejected documents are historical evidence, never mutation menus.
 * DDT→invoice reclassification is disabled for every row until it can be
 * expressed by the same atomic document-transition + StockCommand path.
 */
export function documentDetailActionPolicy(
  source: 'fiscal' | 'public',
  status: string | null | undefined,
  archivedAt: string | null | undefined,
): DocumentDetailActionPolicy {
  return {
    can_mutate: source === 'public'
      ? archivedAt == null && status !== 'archived' && status !== 'rejected'
      : status !== 'rejected',
    can_reclassify: false,
  };
}

export interface CorrectionBalanceEvent {
  id?: unknown;
  server_id?: unknown;
  business_id?: unknown;
  kind?: unknown;
  rev?: unknown;
  client_ts?: unknown;
  payload?: unknown;
}

export interface CorrectionBalanceLocator {
  food_id: string;
  business_id: string;
  allow_legacy_unscoped_business: boolean;
  batch_lot: string | null;
  storage_unit_id: string | null;
  uom: string;
}

export interface CorrectionBalanceExpectation {
  qty: number;
  reserved_qty: number;
  uom: string;
  event_count: number;
  last_event_ref: string;
  last_server_rev: number;
}

function normalizedNullable(value: unknown): string | null {
  return stringOrNull(value)?.trim() ?? null;
}

function normalizedUom(value: string): string {
  return value.trim().toLowerCase();
}

function roundedStockQty(value: number): number {
  return Math.round(value * 1e12) / 1e12;
}

function isCorrectionBalanceCoordinate(
  event: CorrectionBalanceEvent,
  payload: Record<string, unknown>,
  locator: CorrectionBalanceLocator,
): boolean {
  return stockEventMatchesBusiness(
    { business_id: stringOrNull(event.business_id), payload },
    locator.business_id,
    locator.allow_legacy_unscoped_business,
  )
    && payload.food_id === locator.food_id
    && normalizedNullable(payload.batch_lot) === normalizedNullable(locator.batch_lot)
    && normalizedNullable(payload.storage_unit_id)
      === normalizedNullable(locator.storage_unit_id);
}

/**
 * A registry-less correction cannot attribute consumption across arbitrary
 * historical units. The server applies the same fail-closed guard while
 * holding the tenant stock lock.
 */
export function hasCrossUomStockActivity(
  events: readonly CorrectionBalanceEvent[],
  locator: CorrectionBalanceLocator,
): boolean {
  for (const event of events) {
    if (
      event.kind !== 'stock_in'
      && event.kind !== 'stock_out'
      && event.kind !== 'stock_reserved'
    ) continue;
    const payload = recordOrNull(event.payload);
    if (!payload || !isCorrectionBalanceCoordinate(event, payload, locator)) continue;
    const hasStockQty = payload.stock_qty !== undefined && payload.stock_qty !== null;
    const hasStockUom = payload.stock_uom !== undefined && payload.stock_uom !== null;
    if (hasStockQty !== hasStockUom) return true;
    const uom = stringOrNull(hasStockUom ? payload.stock_uom : payload.uom);
    if (!uom || normalizedUom(uom) !== normalizedUom(locator.uom)) return true;
  }
  return false;
}

/**
 * Builds the exact optimistic-lock token that the server re-checks before
 * accepting a correction. Any pending/malformed fact on the same coordinate
 * makes the snapshot unverifiable and therefore blocks the correction.
 */
export function resolveCorrectionBalanceExpectation(
  events: readonly CorrectionBalanceEvent[],
  locator: CorrectionBalanceLocator,
): CorrectionBalanceExpectation | null {
  const matching: Array<{
    id: string;
    rev: number;
    qty: number;
    kind: 'stock_in' | 'stock_out' | 'stock_reserved';
    reserveReleased: boolean;
  }> = [];
  const seen = new Set<string>();

  for (const event of events) {
    if (
      event.kind !== 'stock_in'
      && event.kind !== 'stock_out'
      && event.kind !== 'stock_reserved'
    ) continue;
    const payload = recordOrNull(event.payload);
    if (!payload || !isCorrectionBalanceCoordinate(event, payload, locator)) continue;

    const hasStockQty = payload.stock_qty !== undefined && payload.stock_qty !== null;
    const hasStockUom = payload.stock_uom !== undefined && payload.stock_uom !== null;
    if (hasStockQty !== hasStockUom) return null;
    const qty = numberOrNull(hasStockQty ? payload.stock_qty : payload.qty);
    const uom = stringOrNull(hasStockUom ? payload.stock_uom : payload.uom);
    if (qty === null || qty < 0 || !uom) return null;
    if (normalizedUom(uom) !== normalizedUom(locator.uom)) return null;

    const id = stringOrNull(event.id);
    const rev = numberOrNull(event.rev);
    if (!id || rev === null || !Number.isInteger(rev) || rev <= 0) return null;
    if (seen.has(id)) continue;
    seen.add(id);
    matching.push({
      id,
      rev,
      qty,
      kind: event.kind,
      reserveReleased: payload.reserve_released === true,
    });
  }

  if (matching.length === 0) return null;
  matching.sort((left, right) => left.rev - right.rev || left.id.localeCompare(right.id));
  const qty = roundedStockQty(matching.reduce(
    (sum, event) => (
      event.kind === 'stock_in'
        ? sum + event.qty
        : event.kind === 'stock_out'
          ? sum - event.qty
          : sum
    ),
    0,
  ));
  const reservedQty = roundedStockQty(matching.reduce(
    (sum, event) => (
      event.kind !== 'stock_reserved'
        ? sum
        : sum + (event.reserveReleased ? -event.qty : event.qty)
    ),
    0,
  ));
  if (qty < 0 || reservedQty < 0) return null;
  const last = matching[matching.length - 1];
  return {
    qty,
    reserved_qty: reservedQty,
    uom: locator.uom,
    event_count: matching.length,
    last_event_ref: last.id,
    last_server_rev: last.rev,
  };
}

function sameStockNumber(left: number | null, right: number | null): boolean {
  if (left === null || right === null) return left === right;
  return Math.abs(left - right)
    <= 1e-12 * Math.max(1, Math.abs(left), Math.abs(right));
}

/**
 * Rehydrates the immutable correction source from the confirmed local mirror.
 * The modal snapshot is only an identity/token: every coordinate is checked
 * against the same server-revision event immediately before enqueueing. This
 * prevents a stale render, a pending optimistic event or a business mismatch
 * from becoming the source of a compensating command.
 */
export function resolveConfirmedCorrectionSourceSnapshot(
  events: readonly CorrectionBalanceEvent[],
  candidate: CorrectionSourceSnapshot,
  allowLegacyUnscopedBusiness: boolean,
): CorrectionSourceSnapshot | null {
  const matches = events.filter((event) => {
    const eventId = stringOrNull(event.id);
    const serverId = stringOrNull(event.server_id);
    const rev = numberOrNull(event.rev);
    return event.kind === 'stock_in'
      && rev !== null
      && Number.isInteger(rev)
      && rev > 0
      && rev === candidate.original_event_server_rev
      && (
        eventId === candidate.original_event_client_uuid
        || eventId === candidate.original_event_id
        || serverId === candidate.original_event_id
        || serverId === candidate.original_event_client_uuid
      );
  });
  // Duplicate mirror identities are themselves an integrity problem. Do not
  // pick one by ordering: reconciliation must remove the ambiguity first.
  if (matches.length !== 1) return null;

  const event = matches[0];
  const payload = recordOrNull(event.payload);
  if (!payload || !stockEventMatchesBusiness(
    { business_id: stringOrNull(event.business_id), payload },
    candidate.business_id,
    allowLegacyUnscopedBusiness,
  )) {
    return null;
  }

  const hasStockQty = payload.stock_qty !== undefined && payload.stock_qty !== null;
  const hasStockUom = payload.stock_uom !== undefined && payload.stock_uom !== null;
  if (hasStockQty !== hasStockUom) return null;
  const qty = numberOrNull(hasStockQty ? payload.stock_qty : payload.qty);
  const uom = stringOrNull(hasStockUom ? payload.stock_uom : payload.uom);
  if (qty === null || qty <= 0 || !uom) return null;

  const rawUnitCost = payload.stock_unit_cost !== undefined
    ? payload.stock_unit_cost
    : payload.unit_cost;
  if (rawUnitCost !== null && rawUnitCost !== undefined && numberOrNull(rawUnitCost) === null) {
    return null;
  }
  const unitCost = numberOrNull(rawUnitCost);
  const currency = (
    stringOrNull(payload.source_currency)
    ?? stringOrNull(payload.currency)
    ?? 'EUR'
  ).toUpperCase();
  const commandId = stringOrNull(payload.command_id);
  const commandSeq = numberOrNull(payload.command_seq);
  if (
    (commandId === null) !== (commandSeq === null)
    || (commandSeq !== null && (!Number.isInteger(commandSeq) || commandSeq <= 0))
  ) {
    return null;
  }
  const payloadRoot = stringOrNull(payload.correction_root_event_id);
  const rootEventId = payloadRoot ?? candidate.original_event_id;

  if (
    stringOrNull(payload.food_id) !== candidate.food_id
    || normalizedNullable(payload.batch_lot) !== normalizedNullable(candidate.lot)
    || normalizedNullable(payload.expiration_date)
      !== normalizedNullable(candidate.expiration_date)
    || normalizedNullable(payload.storage_unit_id)
      !== normalizedNullable(candidate.storage_unit_id)
    || normalizedNullable(payload.supplier_id)
      !== normalizedNullable(candidate.supplier_id)
    || stringOrNull(payload.document_id) !== candidate.document_id
    || !sameStockNumber(qty, candidate.qty)
    || normalizedUom(uom) !== normalizedUom(candidate.uom)
    || !sameStockNumber(unitCost, candidate.unit_cost)
    || currency !== candidate.currency.toUpperCase()
    || commandId !== candidate.original_command_id
    || commandSeq !== candidate.original_command_seq
    || rootEventId !== candidate.correction_root_event_id
  ) {
    return null;
  }

  return {
    ...candidate,
    lot: normalizedNullable(payload.batch_lot),
    expiration_date: normalizedNullable(payload.expiration_date),
    qty,
    uom,
    storage_unit_id: normalizedNullable(payload.storage_unit_id),
    supplier_id: normalizedNullable(payload.supplier_id),
    unit_cost: unitCost,
    currency,
    original_command_id: commandId,
    original_command_seq: commandSeq,
    correction_root_event_id: rootEventId,
  };
}

export const correctionConversion = stockCorrectionConversionV1;

/**
 * Legge la copia sorgente conservata in documents.ocr_parsed. Gli eventi sono
 * usati solo per arricchire food_id e coordinate di magazzino; non sovrascrivono
 * quantità, UdM o prezzi documentali presenti nell'OCR.
 */
function sourceLinesFromOcr(
  docId: string,
  ocrParsed: unknown,
  eventLines: DocLine[],
): DocLine[] {
  const root = recordOrNull(ocrParsed);
  const rawLines = root && Array.isArray(root.lines) ? root.lines : [];
  if (rawLines.length === 0) return [];
  const eventsBySeq = new Map<number, DocLine>();
  for (const eventLine of eventLines) {
    eventsBySeq.set(eventLine.source_seq ?? eventLine.seq, eventLine);
  }

  return rawLines.flatMap((raw, index) => {
    const line = recordOrNull(raw);
    if (!line) return [];
    const seq = numberOrNull(line.seq) ?? index + 1;
    const eventLine = eventsBySeq.get(seq) ?? eventLines[index];
    const qty = numberOrNull(line.source_qty)
      ?? numberOrNull(line.qty)
      ?? eventLine?.qty
      ?? 0;
    const uom = stringOrNull(line.source_uom)
      ?? stringOrNull(line.uom)
      ?? eventLine?.uom
      ?? '';
    return [{
      doc_id: docId,
      seq,
      source_seq: seq,
      description: stringOrNull(line.raw_description)
        ?? stringOrNull(line.description)
        ?? eventLine?.description
        ?? '',
      qty,
      uom,
      unit_price: numberOrNull(line.source_unit_price)
        ?? numberOrNull(line.unit_price)
        ?? eventLine?.unit_price
        ?? null,
      line_total: numberOrNull(line.source_line_total)
        ?? numberOrNull(line.line_total)
        ?? eventLine?.line_total
        ?? null,
      batch_lot: stringOrNull(line.batch_lot) ?? eventLine?.batch_lot ?? null,
      expiration_date: stringOrNull(line.expiration_date) ?? eventLine?.expiration_date ?? null,
      iva_rate: numberOrNull(line.iva_rate) ?? eventLine?.iva_rate ?? null,
      food_id: stringOrNull(line.food_id) ?? eventLine?.food_id,
      stock_qty: numberOrNull(line.stock_qty) ?? eventLine?.stock_qty ?? eventLine?.qty ?? qty,
      stock_uom: stringOrNull(line.stock_uom) ?? eventLine?.stock_uom ?? eventLine?.uom ?? uom,
      stock_unit_cost: numberOrNull(line.stock_unit_cost) ?? eventLine?.stock_unit_cost ?? null,
      conversion_factor: numberOrNull(line.conversion_factor) ?? eventLine?.conversion_factor ?? null,
      conversion_source: stringOrNull(line.conversion_source) ?? eventLine?.conversion_source ?? null,
      stock_event_id: eventLine?.stock_event_id,
      stock_event_client_uuid: eventLine?.stock_event_client_uuid,
      stock_event_server_rev: eventLine?.stock_event_server_rev,
      stock_command_id: eventLine?.stock_command_id,
      stock_command_seq: eventLine?.stock_command_seq,
      stock_business_id: eventLine?.stock_business_id,
      stock_storage_unit_id: eventLine?.stock_storage_unit_id,
      supplier_sku: eventLine?.supplier_sku,
      is_frozen: eventLine?.is_frozen,
      notify_expiration: eventLine?.notify_expiration,
      keep_current_lot: eventLine?.keep_current_lot,
      stock_supplier_id: eventLine?.stock_supplier_id,
      stock_batch_lot: eventLine?.stock_batch_lot,
      stock_expiration_date: eventLine?.stock_expiration_date,
    }];
  });
}

interface SupplierInfo {
  id: string;
  name: string;
  piva: string | null;
  category: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
}

interface DocumentDetail {
  doc: FiscalDoc;
  lines: DocLine[];
  supplier: SupplierInfo | null;
  signed_urls: { pdf?: string; image?: string };
  business_scope: DocumentBusinessScope;
  deferred_original: {
    reviewId: string;
    expectedPages: number | null;
  } | null;
}

async function fetchDeferredOriginal(
  sb: ReturnType<typeof getSupabase>,
  tenantId: string | null,
  documentId: string,
  documentPath: string | null,
): Promise<DocumentDetail['deferred_original']> {
  if (!tenantId) return null;
  const deferredPathMatch = documentPath?.match(/\/review-source-deferred\/([0-9a-f-]{36})\.pdf$/i);
  const { data, error } = await sb
    .from('document_import_reviews_v1')
    .select('id,request_payload')
    .eq('tenant_id', tenantId)
    .eq('committed_document_id', documentId)
    .eq('status', 'approved')
    .order('updated_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error || !data) {
    return deferredPathMatch ? { reviewId: deferredPathMatch[1], expectedPages: null } : null;
  }
  const deferred = recordOrNull(recordOrNull(data.request_payload)?.review_original_deferred);
  if (deferred?.status === 'attached') return null;
  if (deferred?.status !== 'pending' && !deferredPathMatch) return null;
  return {
    reviewId: String(data.id),
    expectedPages: numberOrNull(deferred?.expected_pages),
  };
}

async function fetchDoc(source: 'fiscal' | 'public', id: string): Promise<DocumentDetail> {
  const sb = getSupabase();

  if (source === 'public') {
    // DDT in public.documents
    const { data: doc, error: dErr } = await sb
      .from('documents')
      .select('id,tenant_id,business_id,kind,supplier_id,document_number,document_date,file_path,file_mime,ocr_parsed,created_at,archived_at,conformity_status,conformity_notes,conformity_checked_at')
      .eq('id', id)
      .maybeSingle();
    if (dErr) throw dErr;
    if (!doc) throw new Error('Documento non trovato');
    const ddt = doc as { id: string; tenant_id: string | null; business_id: string | null; kind: string; supplier_id: string | null; document_number: string | null; document_date: string | null; file_path: string | null; file_mime: string | null; ocr_parsed: unknown; created_at: string; archived_at: string | null; conformity_status: 'conforme' | 'non_conforme' | 'pending' | null; conformity_notes: string | null; conformity_checked_at: string | null };
    const businessScope = await fetchDocumentBusinessScope(
      sb,
      ddt.tenant_id,
      ddt.business_id,
    );

    // Le righe del DDT sono ricostruibili dagli stock_in con payload.document_id = ddt.id
    let eventsQuery = sb
      .from('events')
      .select('id, client_uuid, business_id, server_rev, payload, client_ts')
      .eq('kind', 'stock_in')
      .eq('payload->>document_id', id)
      .order('client_ts', { ascending: true });
    if (ddt.tenant_id) eventsQuery = eventsQuery.eq('tenant_id', ddt.tenant_id);
    const { data: events } = await eventsQuery;
    const eventLines: DocLine[] = ((events ?? []) as Array<{
      id: string;
      client_uuid: string;
      business_id: string | null;
      server_rev: number;
      payload: Record<string, unknown>;
    }>)
      // T-195: escludi gli stock_in di rettifica (reason='lot_correction'),
      // altrimenti comparirebbero come righe-fantasma del documento.
      .filter((e) => (
        businessScope.business_id !== null
        && e.payload.reason !== 'lot_correction'
        && stockEventMatchesBusiness(
          { business_id: e.business_id, payload: e.payload },
          businessScope.business_id,
          businessScope.allow_legacy_unscoped,
        )
      ))
      .map((e, i) => {
      const p = e.payload as {
        food_id?: string;
        qty?: number;
        uom?: string;
        source_qty?: number;
        source_uom?: string;
        source_unit_price?: number | null;
        source_line_total?: number | null;
        stock_qty?: number;
        stock_uom?: string;
        stock_unit_cost?: number | null;
        conversion_factor?: number | null;
        conversion_source?: string | null;
        doc_qty?: number;
        doc_uom?: string;
        source_seq?: number;
        batch_lot?: string | null;
        expiration_date?: string | null;
        unit_price?: number | null;
        line_total?: number | null;
        unit_cost?: number | null;
        command_id?: string;
        command_seq?: number;
        storage_unit_id?: string | null;
        supplier_sku?: string | null;
        is_frozen?: boolean;
        notify_expiration?: boolean;
        keep_current_lot?: boolean;
        supplier_id?: string | null;
      };
      return {
        doc_id: id, seq: p.source_seq ?? i + 1, source_seq: p.source_seq ?? i + 1,
        description: '', // verrà sostituito sotto col nome alimento
        // Nuovi eventi espongono la riga sorgente; i vecchi ricadono sugli
        // alias doc_* e infine sulle coordinate del movimento.
        qty: p.source_qty ?? p.doc_qty ?? p.qty ?? 0,
        uom: p.source_uom ?? p.doc_uom ?? p.uom ?? '',
        unit_price: p.source_unit_price ?? p.unit_price ?? p.unit_cost ?? null,
        line_total: p.source_line_total ?? p.line_total ?? null,
        batch_lot: p.batch_lot ?? null,
        expiration_date: p.expiration_date ?? null,
        iva_rate: null,
        food_id: p.food_id,
        stock_qty: p.stock_qty ?? p.qty,
        stock_uom: p.stock_uom ?? p.uom,
        stock_unit_cost: p.stock_unit_cost ?? p.unit_cost ?? null,
        conversion_factor: p.conversion_factor ?? null,
        conversion_source: p.conversion_source ?? null,
        stock_event_id: e.id,
        stock_event_client_uuid: e.client_uuid,
        stock_event_server_rev: e.server_rev,
        stock_command_id: p.command_id ?? null,
        stock_command_seq: p.command_seq ?? null,
        stock_business_id: businessScope.business_id,
        stock_storage_unit_id: p.storage_unit_id ?? null,
        supplier_sku: p.supplier_sku,
        is_frozen: p.is_frozen,
        notify_expiration: p.notify_expiration,
        keep_current_lot: p.keep_current_lot,
        stock_supplier_id: p.supplier_id ?? null,
        stock_batch_lot: p.batch_lot ?? null,
        stock_expiration_date: p.expiration_date ?? null,
      };
    });
    // T-481: OCR è autorevole per la rappresentazione documentale. Gli eventi
    // restano il fallback per i DDT storici privi di ocr_parsed.
    const ocrLines = sourceLinesFromOcr(id, ddt.ocr_parsed, eventLines);
    const lines = ocrLines.length > 0 ? ocrLines : eventLines;
    // Risolvi nomi food
    const foodIds = [...new Set(lines.map((l) => l.food_id).filter((x): x is string => !!x))];
    if (foodIds.length) {
      const { data: foods } = await sb.from('foods').select('id,name,default_uom').in('id', foodIds);
      const byId = new Map(((foods ?? []) as Array<{
        id: string;
        name: string;
        default_uom: string;
      }>).map((food) => [food.id, food]));
      for (const l of lines) {
        if (!l.food_id) continue;
        const food = byId.get(l.food_id);
        if (!l.description) l.description = food?.name ?? l.food_id.slice(0, 8);
        l.food_name = food?.name;
        l.food_default_uom = food?.default_uom;
      }
    }

    let supplier: SupplierInfo | null = null;
    if (ddt.supplier_id) {
      const { data: srow } = await sb.from('suppliers')
        .select('id,name,piva,category,email,phone,address')
        .eq('id', ddt.supplier_id)
        .maybeSingle();
      if (srow) supplier = srow as SupplierInfo;
    }

    const deferredOriginal = await fetchDeferredOriginal(sb, ddt.tenant_id, id, ddt.file_path);
    const signed_urls: { pdf?: string; image?: string } = {};
    if (ddt.file_path && !deferredOriginal) {
      const { data: signed } = await sb.storage.from('documents').createSignedUrls([ddt.file_path], 60 * 15);
      const url = signed?.[0]?.signedUrl;
      if (url) {
        if (ddt.file_mime?.startsWith('image/')) signed_urls.image = url;
        else signed_urls.pdf = url;
      }
    }

    const ocrDocument = recordOrNull(recordOrNull(ddt.ocr_parsed)?.document);
    return {
      doc: {
        id: ddt.id,
        tenant_id: ddt.tenant_id,
        business_id: ddt.business_id,
        archived_at: ddt.archived_at,
        type: 'ddt',
        number: ddt.document_number ?? '—',
        date: ddt.document_date ?? ddt.created_at.slice(0, 10),
        total_amount: numberOrNull(ocrDocument?.total_amount),
        currency: stringOrNull(ocrDocument?.currency) ?? 'EUR',
        supplier_id: ddt.supplier_id,
        status: ddt.archived_at ? 'archived' : 'received',
        sdi_id: null,
        received_via_provider: null,
        pdf_file_id: null,
        image_file_id: null,
        xml_file_id: null,
        parsed_at: null,
        notes: null,
        created_at: ddt.created_at,
        conformity_status: ddt.conformity_status,
        conformity_notes: ddt.conformity_notes,
        conformity_checked_at: ddt.conformity_checked_at,
      },
      lines,
      supplier,
      signed_urls,
      business_scope: businessScope,
      deferred_original: deferredOriginal,
    };
  }

  // source === 'fiscal' (fattura)
  const { data: doc, error: dErr } = await sb
    .from('fiscal_documents')
    .select('id,tenant_id,business_id,type,number,date,total_amount,currency,supplier_id,status,sdi_id,received_via_provider,pdf_file_id,image_file_id,xml_file_id,parsed_at,notes,created_at')
    .eq('id', id)
    .maybeSingle();
  if (dErr) throw dErr;
  if (!doc) throw new Error('Documento non trovato');
  const document = doc as FiscalDoc;
  const businessScope = await fetchDocumentBusinessScope(
    sb,
    document.tenant_id,
    document.business_id,
  );

  const { data: lines } = await sb
    .from('fiscal_document_lines')
    .select('doc_id,seq,description,qty,uom,unit_price,line_total,batch_lot,expiration_date,iva_rate,food_id')
    .eq('doc_id', id)
    .order('seq', { ascending: true });

  // T-300: le righe fattura spesso non hanno food_id collegato (l'import non lo
  // popola sulla riga d'archivio) → il tasto "Correggi" per-alimento sparirebbe.
  // Risolviamo il food_id per nome (il food è stato creato dalla riga fattura,
  // stesso nome) così la modifica per singolo alimento torna disponibile.
  let resolvedLines = (lines ?? []) as DocLine[];
  // Le righe fiscali sono ora fedeli alla fattura; recupera dagli eventi le
  // coordinate derivate necessarie alla correzione della giacenza.
  let fiscalStockEventsQuery = sb
    .from('events')
    .select('id,client_uuid,business_id,server_rev,payload,client_ts')
    .eq('kind', 'stock_in')
    .eq('payload->>document_id', id)
    .order('client_ts', { ascending: true });
  if (document.tenant_id) {
    fiscalStockEventsQuery =
      fiscalStockEventsQuery.eq('tenant_id', document.tenant_id);
  }
  const { data: fiscalStockEvents } = await fiscalStockEventsQuery;
  interface FiscalStockEvent {
    id: string;
    client_uuid: string;
    business_id: string | null;
    server_rev: number;
    payload: Record<string, unknown>;
  }
  const stockBySeq = new Map<number, FiscalStockEvent>();
  ((fiscalStockEvents ?? []) as FiscalStockEvent[])
    .filter((event) => (
      businessScope.business_id !== null
      && event.payload.reason !== 'lot_correction'
      && stockEventMatchesBusiness(
        { business_id: event.business_id, payload: event.payload },
        businessScope.business_id,
        businessScope.allow_legacy_unscoped,
      )
    ))
    .forEach((event, index) => {
      const seq = numberOrNull(event.payload.source_seq) ?? index + 1;
      stockBySeq.set(seq, event);
    });
  resolvedLines = resolvedLines.map((line, index) => {
    const stockEvent = stockBySeq.get(line.seq) ?? stockBySeq.get(index + 1);
    if (!stockEvent) return line;
    const stock = stockEvent.payload;
    return {
      ...line,
      food_id: line.food_id ?? stringOrNull(stock.food_id) ?? undefined,
      stock_qty: numberOrNull(stock.stock_qty) ?? numberOrNull(stock.qty) ?? line.qty,
      stock_uom: stringOrNull(stock.stock_uom) ?? stringOrNull(stock.uom) ?? line.uom,
      stock_unit_cost: numberOrNull(stock.stock_unit_cost) ?? numberOrNull(stock.unit_cost),
      conversion_factor: numberOrNull(stock.conversion_factor),
      conversion_source: stringOrNull(stock.conversion_source),
      stock_event_id: stockEvent.id,
      stock_event_client_uuid: stockEvent.client_uuid,
      stock_event_server_rev: stockEvent.server_rev,
      stock_command_id: stringOrNull(stock.command_id),
      stock_command_seq: numberOrNull(stock.command_seq),
      stock_business_id: businessScope.business_id,
      stock_storage_unit_id: stringOrNull(stock.storage_unit_id),
      supplier_sku: stringOrNull(stock.supplier_sku),
      is_frozen: typeof stock.is_frozen === 'boolean' ? stock.is_frozen : undefined,
      notify_expiration: typeof stock.notify_expiration === 'boolean' ? stock.notify_expiration : undefined,
      keep_current_lot: typeof stock.keep_current_lot === 'boolean' ? stock.keep_current_lot : undefined,
      stock_supplier_id: stringOrNull(stock.supplier_id),
      stock_batch_lot: stringOrNull(stock.batch_lot),
      stock_expiration_date: stringOrNull(stock.expiration_date),
    };
  });
  const needFood = resolvedLines.filter((l) => !l.food_id && l.description);
  if (needFood.length) {
    const names = [...new Set(needFood.map((l) => l.description))];
    const { data: foods } = await sb
      .from('foods')
      .select('id,name')
      .eq('tenant_id', document.tenant_id)
      .in('name', names);
    const byName = new Map(((foods ?? []) as Array<{ id: string; name: string }>).map((f) => [f.name, f.id]));
    resolvedLines = resolvedLines.map((l) => (l.food_id ? l : { ...l, food_id: byName.get(l.description) }));
  }
  const resolvedFoodIds = [...new Set(
    resolvedLines.map((line) => line.food_id).filter((foodId): foodId is string => !!foodId),
  )];
  if (resolvedFoodIds.length > 0) {
    const { data: foods } = await sb
      .from('foods')
      .select('id,name,default_uom')
      .eq('tenant_id', document.tenant_id)
      .in('id', resolvedFoodIds);
    const foodById = new Map(((foods ?? []) as Array<{
      id: string;
      name: string;
      default_uom: string;
    }>).map((food) => [food.id, food]));
    resolvedLines = resolvedLines.map((line) => ({
      ...line,
      food_name: line.food_id ? foodById.get(line.food_id)?.name : undefined,
      food_default_uom: line.food_id ? foodById.get(line.food_id)?.default_uom : undefined,
    }));
  }

  let supplier: SupplierInfo | null = null;
  if (document.supplier_id) {
    const { data: srow } = await sb
      .from('suppliers')
      .select('id,name,piva,category,email,phone,address')
      .eq('id', document.supplier_id)
      .maybeSingle();
    if (srow) supplier = srow as SupplierInfo;
  }

  const deferredOriginal = await fetchDeferredOriginal(
    sb,
    document.tenant_id,
    id,
    document.pdf_file_id ?? document.image_file_id,
  );
  const signed_urls: { pdf?: string; image?: string } = {};
  const paths: string[] = [];
  if (document.pdf_file_id) paths.push(document.pdf_file_id);
  if (document.image_file_id) paths.push(document.image_file_id);
  if (paths.length && !deferredOriginal) {
    const { data: signed } = await sb.storage.from('documents').createSignedUrls(paths, 60 * 15);
    for (const s of signed ?? []) {
      if (!s.signedUrl) continue;
      if (s.path === document.pdf_file_id) signed_urls.pdf = s.signedUrl;
      if (s.path === document.image_file_id) signed_urls.image = s.signedUrl;
    }
  }

  return {
    doc: document,
    lines: resolvedLines,
    supplier,
    signed_urls,
    business_scope: businessScope,
    deferred_original: deferredOriginal,
  };
}

export function DocumentDetailPage({ quickLabel = false }: { quickLabel?: boolean } = {}): JSX.Element {
  const { source, id } = useParams<{ source: 'fiscal' | 'public'; id: string }>();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['doc_detail', source, id],
    queryFn: () => fetchDoc(source, id),
    enabled: !!id && (source === 'fiscal' || source === 'public'),
    staleTime: 30_000,
  });
  const [iban, setIban] = useState<string | null>(null);
  const decrypt = useDecryptColumn();
  const { profile } = useAuth();
  const history = useHistory();
  const qc = useQueryClient();
  const enqueueStockCommand = useEnqueueStockCommand();
  const stockInEvents = useEvents('stock_in', null);
  const stockOutEvents = useEvents('stock_out', null);
  const stockReservedEvents = useEvents('stock_reserved', null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [openingFile, setOpeningFile] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [docOpenMode, setDocOpenMode] = useState<DocOpenMode>(loadDocOpenPref);
  const [openSheet, setOpenSheet] = useState(false);

  // T-196: correzione lotto post-commit (append-only, hash-chain compliant).
  // Le rettifiche sono indicizzate per food_id e sovrapposte alle righe documento.
  const tenantIdForCarico = data?.doc.tenant_id ?? profile?.active_tenant_id ?? null;
  const actionPolicy = documentDetailActionPolicy(
    source,
    data?.doc.status,
    data?.doc.archived_at,
  );
  const hasVerifiedBusinessScope = !!data?.business_scope.business_id;
  const canMutateDocument =
    !!data && actionPolicy.can_mutate && hasVerifiedBusinessScope;
  const cancellationConfirmation = data
    ? buildDocumentCancellationConfirmation({
        type: data.doc.type,
        number: data.doc.number === '—' ? null : data.doc.number,
        date: data.doc.date,
        supplierName: data.supplier?.name ?? null,
      })
    : null;
  const mutationBlockedMessage = !actionPolicy.can_mutate
    ? 'Questo documento è storico: non può più modificare il magazzino.'
    : 'La sede originale non è verificabile: le modifiche sono bloccate.';
  const canAttachDeferredOriginal = profile?.is_platform_owner === true
    || ['consulente', 'ristoratore', 'osa'].includes(profile?.role ?? '');
  const corrections = useQuery({
    queryKey: [
      'doc_corrections',
      source,
      id,
      tenantIdForCarico,
      data?.business_scope.business_id,
      data?.business_scope.allow_legacy_unscoped,
    ],
    queryFn: () => fetchCorrections(
      getSupabase(),
      tenantIdForCarico as string,
      id,
      data!.business_scope,
    ),
    enabled:
      !!id
      && !!tenantIdForCarico
      && !!data?.business_scope.business_id,
    staleTime: 15_000,
  });
  function correctionForLine(line: DocLine): LotCorrection | undefined {
    if (!corrections.data) return undefined;
    const byRoot = line.stock_event_id
      ? corrections.data.byRootEventId[line.stock_event_id]
      : undefined;
    if (byRoot) return byRoot;
    if (!line.food_id || !data) return undefined;
    const sameFoodRows = data.lines.filter((candidate) => candidate.food_id === line.food_id);
    return sameFoodRows.length === 1
      ? corrections.data.legacyByFoodId[line.food_id]
      : undefined;
  }
  const [correctTarget, setCorrectTarget] = useState<CorrectTarget | null>(null);
  const [correcting, setCorrecting] = useState(false);
  const correctionSubmitLock = useRef(false);
  // T-331: form della correzione riga (quantità + UdM + lotto + scadenza + motivo).
  // Inizializzato coi valori EFFETTIVI correnti della riga (riga originale o
  // l'ultima rettifica) quando si apre la modale.
  const [correctForm, setCorrectForm] = useState({ qty: '', uom: '', lot: '', expiry: '', reason: '' });
  // Apre la correzione precompilando il form con i valori effettivi della riga.
  function openCorrection(t: CorrectTarget): void {
    setCorrectForm({
      qty: String(t.qty),
      uom: t.canonical_uom,
      lot: t.lot ?? '',
      expiry: (t.expiration_date ?? '').slice(0, 10),
      reason: '',
    });
    setCorrectTarget(t);
  }

  async function handleExportReceipt(): Promise<void> {
    if (!data) return;
    setExportingPdf(true);
    try {
      const input: ReceiptInput = {
        source,
        docId: id,
        docTypeLabel: data.doc.type.toUpperCase(),
        docNumber: data.doc.number,
        docDateIso: data.doc.date,
        docStatus: data.doc.status,
        totalAmount: data.doc.total_amount,
        currency: data.doc.currency,
        supplier: data.supplier
          ? {
              name: data.supplier.name,
              piva: data.supplier.piva,
              address: data.supplier.address,
              category: data.supplier.category,
            }
          : null,
        lines: data.lines.map((l) => ({
          description: l.description,
          qty: l.qty,
          uom: l.uom,
          unitPrice: l.unit_price,
          lineTotal: l.line_total,
          batchLot: l.batch_lot,
          expirationIso: l.expiration_date,
        })),
        signedUrl: data.signed_urls.pdf ?? data.signed_urls.image ?? null,
        signedIsPdf: !!data.signed_urls.pdf,
        // Use the DOCUMENT's own tenant — not the active tenant — so a
        // multi-tenant/owner user exporting another tenant's document gets the
        // correct responsabile/business/anchor (never a false attribution).
        tenantId: data.doc.tenant_id ?? profile?.active_tenant_id ?? null,
        // La sede è una coordinata del documento, non della sessione corrente:
        // aprire il documento della sede A mentre è attiva B non deve produrre
        // un riepilogo HACCP intestato falsamente a B.
        businessId: documentBusinessIdForOperation(
          data.business_scope.business_id,
          profile?.active_business_id,
        ),
        self: { id: profile?.id ?? null, fullName: profile?.full_name ?? null },
      };
      const { model, scan } = await gatherReceiptData(input);
      const bytes = await buildReceiptPdf(model, scan);
      const safeNum = data.doc.number.replace(/[^a-zA-Z0-9_-]/g, '') || 'doc';
      await exportReceiptPdf(bytes, `riepilogo_ricevimento_${safeNum}_${Date.now()}.pdf`);
      if (!Capacitor.isNativePlatform()) setToast('Riepilogo PDF scaricato');
    } catch (err) {
      // Swallow the native share-sheet "cancel" (not an error worth a toast).
      if (!isShareCancellation(err)) setToast(toUserMessage(err));
    } finally {
      setExportingPdf(false);
    }
  }

  async function handleOpenFile(modeOverride?: DocOpenMode): Promise<void> {
    if (!data) return;
    const url = data.signed_urls.pdf ?? data.signed_urls.image;
    if (!url) return;
    const mode = modeOverride ?? docOpenMode;
    setOpeningFile(true);
    try {
      if (mode === 'inapp') await openInAppViewer(url);
      else await openFileNative(url, !!data.signed_urls.pdf);
    } catch (err) {
      // Lo "Annulla" sullo share sheet non è un errore da notificare.
      if (!isShareCancellation(err)) setToast(toUserMessage(err));
    } finally {
      setOpeningFile(false);
    }
  }

  // T-189: imposta la modalità come predefinita (ricordata) e apre subito.
  function chooseAndOpen(mode: DocOpenMode): void {
    setDocOpenMode(mode);
    try { localStorage.setItem(DOC_OPEN_PREF_KEY, mode); } catch { /* best-effort */ }
    setOpenSheet(false);
    void handleOpenFile(mode);
  }

  async function handleDelete(): Promise<void> {
    if (!data || !canMutateDocument) {
      setToast(mutationBlockedMessage);
      return;
    }
    try {
      const sb = getSupabase();
      const tenantId = data.doc.tenant_id ?? profile?.active_tenant_id ?? null;
      if (!tenantId) throw new Error('tenant_non_disponibile');
      const operationBusinessId = documentBusinessIdForOperation(
        data.business_scope.business_id,
        profile?.active_business_id,
      );
      if (!operationBusinessId) {
        throw new Error(
          'Sede originale non verificabile: annullamento bloccato senza modifiche.',
        );
      }
      const deviceId = await getDeviceId();
      // T-199: "Annulla importazione" con ROLLBACK del magazzino. Usa il
      // mode='cancel' di fiscal-photo (rollback_document_for_replace + stock_out
      // compensativi append-only + cleanup Storage + reset foods.current_*)
      // invece di delete_document, che cancellava solo il record lasciando i
      // lotti orfani in magazzino.
      const { data: result, error } = await sb.functions.invoke('fiscal-photo', {
        body: {
          mode: 'cancel',
          tenant_id: tenantId,
          business_id: operationBusinessId,
          document_id: id,
          document_source: source,
          device_id: deviceId,
          operator_assertion: getActiveOperatorAssertion() ?? undefined,
        },
      });
      if (error) throw error;
      const r = result as { compensated_stock_ins?: number } | null;
      const n = r?.compensated_stock_ins ?? 0;
      setToast(n > 0
        ? `Importazione annullata · ${n} carico/i stornato/i · saldo documento azzerato`
        : 'Importazione annullata');
      // I compensating stock_out + il reset foods sono lato server: tiriamoli
      // giù subito così il magazzino locale (offline-first) si riallinea.
      void getSyncEngine().tick().catch(() => undefined);
      void qc.invalidateQueries({ queryKey: ['fiscal_archive'] });
      void qc.invalidateQueries({ queryKey: ['today_recent_docs'] });
      void qc.invalidateQueries({ queryKey: ['warehouse'] });
      void qc.invalidateQueries({ queryKey: ['foods'] });
      setTimeout(() => history.replace('/documents'), 800);
    } catch (err) {
      setToast(toUserMessage(err));
    }
  }

  // T-201: "Ri-estrai con AI" — rilegge il file salvato del documento con un
  // NUOVO round AI, aprendo il wizard di revisione in modalità SOSTITUZIONE
  // (al commit il vecchio import viene annullato e rimpiazzato). Caso d'uso:
  // l'IA ha sbagliato un prodotto/lotto/totale → rifaccio l'estrazione.
  function handleReextract(): void {
    if (!data || !canMutateDocument) {
      setToast(mutationBlockedMessage);
      return;
    }
    const documentBusinessId = documentBusinessIdForOperation(
      data.business_scope.business_id,
      profile?.active_business_id,
    );
    if (!documentBusinessId) {
      setToast(
        'La sede originale del documento non è disponibile. '
        + 'La ri-estrazione è bloccata per evitare di spostare il carico in un altro locale.',
      );
      return;
    }
    const fileUrl = data.signed_urls.pdf ?? data.signed_urls.image ?? null;
    if (!fileUrl) {
      setToast('Nessun file salvato da rileggere per questo documento.');
      return;
    }
    const mime = data.signed_urls.pdf ? 'application/pdf' : 'image/jpeg';
    try {
      sessionStorage.setItem('trackeat_reextract', JSON.stringify({
        docId: id,
        source,
        businessId: documentBusinessId,
        fileUrl,
        mime,
        docKind: source === 'public' ? 'ddt' : 'fattura',
      }));
    } catch {
      setToast('Impossibile avviare la ri-estrazione su questo dispositivo.');
      return;
    }
    history.push('/documents/capture?reextract=1');
  }

  // T-375 P2: "Correggi importazione" — riapre la review a semaforo PRE-COMPILATA
  // dai carichi già registrati (righe ricostruite dagli stock_in), SENZA rilanciare
  // l'AI. Passa comunque il file per il commit-replace (stesso meccanismo di
  // reextract) e marca correct_import così CapturePhotoPage salta la cattura/AI.
  function handleCorrectImport(): void {
    if (!data || !canMutateDocument) {
      setToast(mutationBlockedMessage);
      return;
    }
    const documentBusinessId = documentBusinessIdForOperation(
      data.business_scope.business_id,
      profile?.active_business_id,
    );
    if (!documentBusinessId) {
      setToast(
        'La sede originale del documento non è disponibile. '
        + 'La correzione è bloccata per evitare di spostare il carico in un altro locale.',
      );
      return;
    }
    const fileUrl = data.signed_urls.pdf ?? data.signed_urls.image ?? null;
    const mime = data.signed_urls.pdf ? 'application/pdf' : 'image/jpeg';
    try {
      sessionStorage.setItem('trackeat_correct_import', JSON.stringify({
        docId: id,
        source,
        businessId: documentBusinessId,
        fileUrl,
        mime,
        docKind: source === 'public' ? 'ddt' : 'fattura',
        document: {
          number: data.doc.number,
          date: data.doc.date,
          type: source === 'public' ? 'ddt' : 'fattura',
          currency: data.doc.currency,
          // La correzione di una fattura riusa lo stesso commit atomico del
          // documento originale: il totale è una precondizione economica e
          // non può essere perso nel passaggio via sessionStorage.
          total_amount: data.doc.total_amount,
        },
        supplier_id: data.supplier?.id ?? null,
        lines: data.lines.map((l) => ({
          seq: l.seq,
          food_id: l.food_id ?? null,
          description: l.description,
          source_qty: l.qty,
          source_uom: l.uom,
          source_unit_price: l.unit_price,
          source_line_total: l.line_total,
          currency: data.doc.currency,
          stock_qty: l.stock_qty ?? l.qty,
          stock_uom: l.stock_uom ?? l.uom,
          stock_unit_cost: l.stock_unit_cost ?? null,
          conversion_factor: l.conversion_factor ?? null,
          conversion_source: l.conversion_source ?? null,
          qty: l.stock_qty ?? l.qty,
          uom: l.stock_uom ?? l.uom,
          batch_lot: l.stock_batch_lot ?? l.batch_lot,
          expiration_date: l.stock_expiration_date ?? l.expiration_date,
          storage_unit_id: l.stock_storage_unit_id,
          supplier_sku: l.supplier_sku,
          is_frozen: l.is_frozen,
          notify_expiration: l.notify_expiration,
          keep_current_lot: l.keep_current_lot,
          iva_rate: l.iva_rate,
          unit_price: l.unit_price,
          line_total: l.line_total,
        })),
      }));
    } catch {
      setToast('Impossibile avviare la correzione su questo dispositivo.');
      return;
    }
    history.push('/documents/capture?correct_import=1');
  }

  // T-481: la correzione è un solo StockCommand append-only. Il comando lega
  // movimento sorgente, saldo/versione osservati e destinazione; il server
  // rifiuta atomicamente coordinate vecchie o cambiate nel frattempo.
  async function submitCorrection(): Promise<void> {
    const target = correctTarget;
    if (!target || !tenantIdForCarico || correcting || correctionSubmitLock.current) return;
    if (!canMutateDocument) {
      setToast(mutationBlockedMessage);
      return;
    }
    const newLot = correctForm.lot.trim();
    const reason = correctForm.reason.trim();
    const newUom = correctForm.uom.trim();
    const newQty = Number(correctForm.qty.replace(',', '.'));
    if (!Number.isFinite(newQty) || newQty <= 0) { setToast('Inserisci una quantità valida (> 0)'); return; }
    if (!newUom) { setToast('Scegli l’unità di misura'); return; }
    if (normalizedUom(newUom) !== normalizedUom(target.canonical_uom)) {
      setToast(
        `La UdM corretta deve essere ${target.canonical_uom}, `
        + 'che è la UdM di magazzino dell’alimento.',
      );
      return;
    }
    if (!reason) { setToast('Indica il motivo della correzione'); return; }
    const newExpiry = correctForm.expiry.trim() || null;
    correctionSubmitLock.current = true;
    setCorrecting(true);
    try {
      const tenantId = tenantIdForCarico;
      if (profile?.active_tenant_id !== tenantId) {
        throw new Error('Apri il documento dal tenant attivo prima di correggere il carico.');
      }
      if (
        stockInEvents.isLoading
        || stockOutEvents.isLoading
        || stockReservedEvents.isLoading
        || stockInEvents.isError
        || stockOutEvents.isError
        || stockReservedEvents.isError
      ) {
        throw new Error('Saldo magazzino non verificabile. Sincronizza e riprova.');
      }
      const balanceEvents = [
        ...(stockInEvents.data ?? []),
        ...(stockOutEvents.data ?? []),
        ...(stockReservedEvents.data ?? []),
      ] as CorrectionBalanceEvent[];
      const confirmedSource = resolveConfirmedCorrectionSourceSnapshot(
        balanceEvents,
        target,
        data?.business_scope.allow_legacy_unscoped ?? false,
      );
      if (!confirmedSource) {
        throw new Error(
          'Movimento originale o sede non verificabili sul dispositivo. '
          + 'Sincronizza e riprova: nessuna rettifica è stata accodata.',
        );
      }
      const balanceLocator = {
        food_id: confirmedSource.food_id,
        business_id: confirmedSource.business_id,
        allow_legacy_unscoped_business:
          data?.business_scope.allow_legacy_unscoped ?? false,
        batch_lot: confirmedSource.lot,
        storage_unit_id: confirmedSource.storage_unit_id,
        uom: confirmedSource.uom,
      };
      if (hasCrossUomStockActivity(balanceEvents, balanceLocator)) {
        throw new Error(
          'Lo storico del lotto contiene movimenti in unità di misura diverse. '
          + 'La correzione singola è bloccata perché i consumi non sono attribuibili '
          + 'con certezza; usa “Correggi importazione”.',
        );
      }
      const expectedBalance = resolveCorrectionBalanceExpectation(
        balanceEvents,
        balanceLocator,
      );
      const balanceTolerance = Number.EPSILON * 16
        * Math.max(
          1,
          Math.abs(expectedBalance?.qty ?? 0),
          Math.abs(confirmedSource.qty),
        );
      if (!expectedBalance) {
        throw new Error('Saldo o versione del lotto non verificabili. Sincronizza e riprova.');
      }
      if (expectedBalance.reserved_qty > 0) {
        throw new Error(
          'Il lotto ha quantità riservate da una produzione: rilascia o chiudi '
          + 'la riserva prima di correggere il carico.',
        );
      }
      if (expectedBalance.qty + balanceTolerance < confirmedSource.qty) {
        throw new Error(
          'Il carico è già stato consumato in parte: la correzione singola è bloccata '
          + 'per non creare una giacenza negativa.',
        );
      }
      const operatorName = profile?.full_name?.trim()
        || profile?.email?.trim()
        || 'Operatore';
      const lineRef = `${source}:${id}:${confirmedSource.original_event_id}`;
      const command = buildStockCorrectionCommandV1({
        commandId: crypto.randomUUID(), tenantId, reason,
        lineRef,
        operatorName, documentNumber: stringOrNull(data?.doc.number),
        source: {
          food_id: confirmedSource.food_id, business_id: confirmedSource.business_id,
          batch_lot: confirmedSource.lot, expiration_date: confirmedSource.expiration_date,
          supplier_id: confirmedSource.supplier_id, document_id: confirmedSource.document_id,
          storage_unit_id: confirmedSource.storage_unit_id, qty: confirmedSource.qty,
          uom: confirmedSource.uom, unit_cost: confirmedSource.unit_cost,
          currency: confirmedSource.currency, original_event_id: confirmedSource.original_event_id,
          original_command_id: confirmedSource.original_command_id,
          original_command_seq: confirmedSource.original_command_seq,
          correction_root_event_id: confirmedSource.correction_root_event_id,
          expectedBalance,
        },
        target: { batchLot: newLot || null, expirationDate: newExpiry, qty: newQty, uom: newUom },
        conversionConfirmation: {
          factor: newQty / confirmedSource.qty,
          referenceId: lineRef,
        },
      });
      await enqueueStockCommand.mutateAsync(command);
      setCorrectTarget(null);
      setToast('Correzione accodata come operazione atomica. Il carico originale resta tracciato.');
      void corrections.refetch();
      void qc.invalidateQueries({ queryKey: ['warehouse'] });
      void qc.invalidateQueries({ queryKey: ['foods'] });
    } catch (err) {
      setToast(toUserMessage(err));
    } finally {
      correctionSubmitLock.current = false;
      setCorrecting(false);
    }
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">{quickLabel ? <IonButton onClick={() => history.replace('/documents')}>Indietro</IonButton> : <IonBackButton defaultHref="/documents" />}</IonButtons>
          <IonTitle>Documento</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        {isLoading && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
            <IonSpinner name="dots" />
          </div>
        )}
        {error && (
          <IonItem color="danger">
            <IonLabel className="ion-text-wrap">
              <h3>Errore</h3>
              <p>{(error as Error).message}</p>
            </IonLabel>
            <IonButton slot="end" fill="outline" onClick={() => void refetch()}>Riprova</IonButton>
          </IonItem>
        )}
        {data && (
          <>
            <IonList inset>
              <IonItem lines="none">
                <IonIcon icon={documentTextOutline} slot="start" color="primary" />
                <IonLabel>
                  <h2 style={{ fontWeight: 700 }}>
                    {data.doc.type.toUpperCase()} N. {data.doc.number}
                  </h2>
                  <p>{new Date(data.doc.date).toLocaleDateString('it-IT')}</p>
                  <p style={{ fontSize: 18, fontWeight: 600, color: '#0066cc', marginTop: 4 }}>
                    {data.doc.total_amount != null
                      ? `€ ${data.doc.total_amount.toFixed(2)} ${data.doc.currency !== 'EUR' ? data.doc.currency : ''}`
                      : <span style={{ fontSize: 13, color: '#888' }}>Importo non riportato (DDT)</span>}
                  </p>
                </IonLabel>
                <IonBadge slot="end" color={data.doc.status === 'matched' || data.doc.status === 'reconciled' ? 'success' : data.doc.status === 'rejected' ? 'danger' : 'medium'}>
                  {data.doc.status}
                </IonBadge>
              </IonItem>
              {(data.signed_urls.pdf || data.signed_urls.image) && (
                <IonItem button detail={false} disabled={openingFile} onClick={() => void handleOpenFile()}>
                  <IonIcon icon={openOutline} slot="start" color="primary" />
                  <IonLabel>
                    <h3>{openingFile ? 'Apertura...' : 'Apri file originale'}</h3>
                    <p style={{ fontSize: 12 }}>
                      {data.signed_urls.pdf ? 'PDF' : 'Immagine'} ·{' '}
                      {docOpenMode === 'inapp' ? 'anteprima nell’app' : 'apri con un’altra app'}
                    </p>
                  </IonLabel>
                  <IonButton
                    fill="clear"
                    size="small"
                    slot="end"
                    aria-label="Scegli come aprire"
                    onClick={(e) => { e.stopPropagation(); setOpenSheet(true); }}
                  >
                    <IonIcon icon={swapHorizontalOutline} slot="icon-only" />
                  </IonButton>
                </IonItem>
              )}
              {/* T-182: export "Scheda riepilogo ricevimento merce" — PDF standalone
                  con dati documento + firma elettronica/hash del carico e della
                  conformità + scansione originale in coda. Sempre disponibile,
                  anche senza scansione (i dati + firma restano un record valido). */}
              <IonItem button detail disabled={exportingPdf} onClick={() => void handleExportReceipt()}>
                <IonIcon icon={downloadOutline} slot="start" color="success" />
                <IonLabel className="ion-text-wrap">
                  <h3>{exportingPdf ? 'Generazione PDF...' : 'Esporta riepilogo (PDF)'}</h3>
                  <p style={{ fontSize: 12 }}>
                    Scheda riassuntiva con dati, firma elettronica e hash del carico — valida per un controllo
                  </p>
                </IonLabel>
              </IonItem>
              {data.doc.sdi_id && (
                <IonItem>
                  <IonLabel>SDI ID</IonLabel>
                  <IonNote slot="end">{data.doc.sdi_id}</IonNote>
                </IonItem>
              )}
              {data.doc.received_via_provider && (
                <IonItem>
                  <IonLabel>Provider</IonLabel>
                  <IonNote slot="end">{data.doc.received_via_provider}</IonNote>
                </IonItem>
              )}
              {data.doc.parsed_at && (
                <IonItem>
                  <IonLabel>Estratto il</IonLabel>
                  <IonNote slot="end">{new Date(data.doc.parsed_at).toLocaleString('it-IT')}</IonNote>
                </IonItem>
              )}
              {data.doc.notes && (
                <IonItem>
                  <IonLabel className="ion-text-wrap">
                    <h3>Note</h3>
                    <p>{data.doc.notes}</p>
                  </IonLabel>
                </IonItem>
              )}
              {/* T-061: badge controllo conformità HACCP al ricevimento. Mostrato
                  solo per DDT (le fatture sono in fiscal_archive, schema senza
                  queste colonne). NULL = documento legacy o flow saltato. */}
              {data.doc.conformity_status && (
                <IonItem lines={data.doc.conformity_notes ? 'inset' : 'none'}>
                  <IonLabel className="ion-text-wrap">
                    <h3>Controllo conformità ricevimento</h3>
                    {data.doc.conformity_checked_at && (
                      <p style={{ fontSize: 12 }}>
                        Verificato il {new Date(data.doc.conformity_checked_at).toLocaleString('it-IT')}
                      </p>
                    )}
                  </IonLabel>
                  <IonBadge
                    slot="end"
                    color={data.doc.conformity_status === 'conforme' ? 'success' : 'warning'}
                  >
                    {data.doc.conformity_status === 'conforme' ? '✅ Conforme' : '⚠️ Non conforme'}
                  </IonBadge>
                </IonItem>
              )}
              {data.doc.conformity_status === 'non_conforme' && data.doc.conformity_notes && (
                <IonItem>
                  <IonLabel className="ion-text-wrap">
                    <h3 style={{ color: '#b45309' }}>Non conformità riscontrate</h3>
                    <p style={{ whiteSpace: 'pre-wrap' }}>{data.doc.conformity_notes}</p>
                  </IonLabel>
                </IonItem>
              )}
            </IonList>

            {data.deferred_original && data.doc.tenant_id && canAttachDeferredOriginal && (
              <ReviewOriginalRecoveryPanel
                tenantId={data.doc.tenant_id}
                reviewId={data.deferred_original.reviewId}
                expectedPages={data.deferred_original.expectedPages}
                mode="attach"
                onRecovered={() => {
                  setToast('Originale aggiunto. Il carico di magazzino non è stato modificato.');
                  void refetch();
                }}
              />
            )}

            {data.deferred_original && !canAttachDeferredOriginal && (
              <IonItem color="warning" lines="none">
                <IonLabel className="ion-text-wrap">
                  <h3>Originale da integrare</h3>
                  <p>Il magazzino è già stato caricato. Un consulente, OSA o ristoratore può aggiungere il file originale dalla scheda documento.</p>
                </IonLabel>
              </IonItem>
            )}

            {/* Anteprima file caricato — immagine inline, tap scarica e apre nel viewer nativo */}
            {(data.signed_urls.pdf || data.signed_urls.image) && (
              <button
                type="button"
                disabled={openingFile}
                onClick={() => void handleOpenFile()}
                style={{
                  display: 'block', width: '100%', margin: '0 0 16px', padding: 0,
                  border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden',
                  background: '#fafafa', cursor: 'pointer', position: 'relative',
                  opacity: openingFile ? 0.6 : 1,
                }}
              >
                <div style={{ height: 220, position: 'relative' }}>
                  {data.signed_urls.image ? (
                    <img
                      src={data.signed_urls.image}
                      alt="Anteprima documento"
                      style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                    />
                  ) : (
                    <div style={{
                      width: '100%', height: '100%',
                      display: 'flex', flexDirection: 'column',
                      alignItems: 'center', justifyContent: 'center',
                      color: '#666', gap: 8,
                    }}>
                      <IonIcon icon={documentTextOutline} style={{ fontSize: 48, color: '#0066cc' }} />
                      <span style={{ fontSize: 14, fontWeight: 600 }}>Documento PDF</span>
                    </div>
                  )}
                  <div style={{
                    position: 'absolute', inset: 0,
                    background: 'linear-gradient(180deg, rgba(0,0,0,0) 60%, rgba(0,0,0,0.55) 100%)',
                    display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
                    padding: 12, color: '#fff', fontSize: 13, fontWeight: 600,
                  }}>
                    {openingFile ? 'Scaricamento in corso...' : 'Tocca per aprire'}
                  </div>
                </div>
              </button>
            )}

            {data.supplier && (
              <IonList inset>
                <IonListHeader><IonLabel>Fornitore</IonLabel></IonListHeader>
                <IonItem>
                  <IonIcon icon={businessOutline} slot="start" />
                  <IonLabel className="ion-text-wrap">
                    <h3>{data.supplier.name}</h3>
                    {data.supplier.piva && <p>P.IVA {data.supplier.piva}</p>}
                    {data.supplier.address && <p style={{ fontSize: 12 }}>{data.supplier.address}</p>}
                    {data.supplier.email && <p style={{ fontSize: 12 }}>{data.supplier.email}</p>}
                    {data.supplier.phone && <p style={{ fontSize: 12 }}>{data.supplier.phone}</p>}
                  </IonLabel>
                  {data.supplier.category && (
                    <IonBadge slot="end" color="medium">{data.supplier.category}</IonBadge>
                  )}
                </IonItem>
                <IonItem>
                  <IonLabel>
                    <h3>IBAN (cifrato)</h3>
                    <p style={{ fontFamily: 'monospace', fontSize: 12 }}>
                      {iban ?? '••••••••••••••••'}
                    </p>
                  </IonLabel>
                  <IonButton
                    slot="end" fill="outline" size="small"
                    disabled={decrypt.isPending || !data.supplier}
                    onClick={() => {
                      if (!data.supplier) return;
                      decrypt.mutate(
                        { table: 'suppliers', row_id: data.supplier.id, column: 'iban_encrypted' },
                        { onSuccess: (v) => setIban(v ?? '— vuoto —') },
                      );
                    }}
                  >
                    <IonIcon icon={eyeOutline} slot="start" />
                    {decrypt.isPending ? '...' : 'Mostra'}
                  </IonButton>
                </IonItem>
              </IonList>
            )}


            <IonList inset>
              <IonListHeader>
                <IonLabel>Righe documento ({data.lines.length})</IonLabel>
              </IonListHeader>
              {data.lines.length === 0 ? (
                <IonItem><IonLabel color="medium">Nessuna riga estratta.</IonLabel></IonItem>
              ) : (
                data.lines.map((l) => {
                  const displayNames = documentLineDisplayNames(l);
                  // T-196/T-331: se questa riga ha avuto una rettifica, mostra i
                  // valori corretti (lotto/scadenza/quantità/UdM). L'originale
                  // resta tracciato come "era: …".
                  const corr = correctionForLine(l);
                  const shownLot = corr ? corr.lot : l.batch_lot;
                  const shownExp = corr ? corr.expiry : l.expiration_date;
                  const shownQty = corr && corr.qty != null ? corr.qty : l.qty;
                  const shownUom = corr && corr.uom != null ? corr.uom : l.uom;
                  const originalStockQty = l.stock_qty ?? l.qty;
                  const originalStockUom = l.stock_uom ?? l.uom;
                  const correctionQty = corr && corr.qty != null ? corr.qty : originalStockQty;
                  const correctionUom = corr && corr.uom != null ? corr.uom : originalStockUom;
                  const originalStockLot = l.stock_batch_lot !== undefined
                    ? l.stock_batch_lot
                    : l.batch_lot;
                  const originalStockExpiry = l.stock_expiration_date !== undefined
                    ? l.stock_expiration_date
                    : l.expiration_date;
                  const correctionLot = corr ? corr.lot : originalStockLot;
                  const correctionExpiry = corr ? corr.expiry : originalStockExpiry;
                  const qtyChanged = corr && corr.qty != null
                    && (corr.qty !== originalStockQty || corr.uom !== originalStockUom);
                  const hasDerivedStock =
                    originalStockQty !== l.qty || originalStockUom !== l.uom;
                  const sourceEventId = corr?.event_id ?? l.stock_event_id ?? null;
                  const sourceEventClientUuid =
                    corr?.event_client_uuid ?? l.stock_event_client_uuid ?? null;
                  const sourceEventServerRev =
                    corr?.event_server_rev ?? l.stock_event_server_rev ?? null;
                  const sourceBusinessId = corr?.business_id ?? l.stock_business_id ?? null;
                  const rootEventId = corr?.root_event_id ?? l.stock_event_id ?? null;
                  const candidateCommandId = corr?.command_id ?? l.stock_command_id ?? null;
                  const candidateCommandSeq = corr?.command_seq ?? l.stock_command_seq ?? null;
                  const hasCommandPair = candidateCommandId !== null
                    && candidateCommandSeq !== null
                    && Number.isInteger(candidateCommandSeq)
                    && candidateCommandSeq > 0;
                  const canCorrect = !!(
                    canMutateDocument
                    && l.food_id
                    && sourceEventId
                    && sourceEventClientUuid
                    && sourceEventServerRev
                    && sourceEventServerRev > 0
                    && sourceBusinessId
                    && rootEventId
                    && l.food_default_uom?.trim()
                    && correctionQty > 0
                    && correctionUom.trim()
                  );
                  return (
                  <IonItem key={`${l.doc_id}-${l.seq}`}>
                    <IonLabel className="ion-text-wrap">
                      <h3>Nome sul documento: {displayNames.documentName}</h3>
                      <p style={{ fontSize: 13, color: '#1d4ed8', fontWeight: 650 }}>
                        Caricato in magazzino: {displayNames.warehouseName}
                      </p>
                      <p style={qtyChanged ? { color: '#1a7f37', fontWeight: 600 } : undefined}>
                        {shownQty} {shownUom}
                        {l.unit_price != null ? ` · € ${l.unit_price.toFixed(2)}/${l.uom}` : ''}
                        {l.line_total != null ? ` · totale € ${l.line_total.toFixed(2)}` : ''}
                        {l.iva_rate != null ? ` · IVA ${l.iva_rate}%` : ''}
                      </p>
                      {!corr && hasDerivedStock && (
                        <p style={{ fontSize: 12, color: '#475569' }}>
                          Carico magazzino: {originalStockQty} {originalStockUom}
                          {l.stock_unit_cost != null
                            ? ` · costo € ${formatMoney(l.stock_unit_cost)}/${originalStockUom}`
                            : ''}
                        </p>
                      )}
                      {(shownLot || shownExp) && (
                        <p style={{ fontSize: 12, color: corr ? '#1a7f37' : '#666' }}>
                          {shownLot && (
                            <>
                              <IonIcon icon={pricetagOutline} style={{ verticalAlign: -2 }} /> Lotto {shownLot}
                            </>
                          )}
                          {shownLot && shownExp ? ' · ' : ''}
                          {shownExp && `Scade ${new Date(shownExp).toLocaleDateString('it-IT')}`}
                        </p>
                      )}
                      {corr && (
                        <p style={{ fontSize: 11, color: '#1a7f37' }}>
                          <IonIcon icon={checkmarkCircleOutline} style={{ verticalAlign: -2 }} />{' '}
                          Corretto il {new Date(corr.at).toLocaleDateString('it-IT')}
                          {qtyChanged ? ` · era: ${originalStockQty} ${originalStockUom}` : (corr.from ? ` · era: ${corr.from}` : '')}
                          {corr.note ? ` · ${corr.note}` : ''}
                        </p>
                      )}
                      {canMutateDocument && l.food_id && !canCorrect && (
                        <p style={{ fontSize: 11, color: '#b45309' }}>
                          Correzione singola bloccata: coordinate storiche non verificabili.
                          Usa “Correggi importazione”.
                        </p>
                      )}
                    </IonLabel>
                    {canMutateDocument && l.food_id && (
                      <IonButton
                        slot="end"
                        fill="clear"
                        size="small"
                        disabled={!canCorrect}
                        onClick={() => openCorrection({
                          food_id: l.food_id!,
                          food_name: l.food_name || l.description,
                          lot: correctionLot,
                          expiration_date: correctionExpiry,
                          qty: correctionQty,
                          uom: correctionUom,
                          canonical_uom: l.food_default_uom!,
                          business_id: sourceBusinessId!,
                          storage_unit_id: corr?.storage_unit_id ?? l.stock_storage_unit_id ?? null,
                          supplier_id: corr?.supplier_id ?? l.stock_supplier_id ?? null,
                          unit_cost: corr?.unit_cost ?? l.stock_unit_cost ?? null,
                          currency: (corr?.currency ?? data.doc.currency ?? 'EUR').toUpperCase(),
                          document_id: id,
                          original_event_id: sourceEventId!,
                          original_event_client_uuid: sourceEventClientUuid!,
                          original_event_server_rev: sourceEventServerRev!,
                          original_command_id: hasCommandPair ? candidateCommandId : null,
                          original_command_seq: hasCommandPair ? candidateCommandSeq : null,
                          correction_root_event_id: rootEventId!,
                        })}
                      >
                        <IonIcon icon={createOutline} slot="start" />
                        Correggi
                      </IonButton>
                    )}
                  </IonItem>
                  );
                })
              )}
            </IonList>

            {canMutateDocument ? (
              <IonList inset>
                <IonListHeader><IonLabel>Azioni</IonLabel></IonListHeader>
                {(data.signed_urls.pdf || data.signed_urls.image) && (
                  <IonItem button onClick={handleReextract}>
                    <IonIcon icon={sparklesOutline} slot="start" color="primary" />
                    <IonLabel>
                      <h3>Ri-estrai con AI</h3>
                      <p style={{ fontSize: 12 }}>Rileggi il documento con l'IA e sostituisci l'estrazione attuale (se l'IA ha sbagliato)</p>
                    </IonLabel>
                  </IonItem>
                )}
                <IonItem button onClick={handleCorrectImport}>
                  <IonIcon icon={createOutline} slot="start" color="primary" />
                  <IonLabel>
                    <h3>Correggi importazione</h3>
                    <p style={{ fontSize: 12 }}>Riapri e correggi i dati caricati (senza rileggere la foto)</p>
                  </IonLabel>
                </IonItem>
                {source === 'public' && (
                  <IonItem>
                    <IonIcon icon={swapHorizontalOutline} slot="start" color="warning" />
                    <IonLabel className="ion-text-wrap">
                      <h3>Riclassifica come fattura non disponibile</h3>
                      <p style={{ fontSize: 12 }}>
                        La procedura precedente spostava il documento fuori dal percorso
                        atomico del magazzino. È bloccata: nessun dato viene modificato.
                      </p>
                    </IonLabel>
                  </IonItem>
                )}
                <IonItem button onClick={() => setConfirmDelete(true)}>
                  <IonIcon icon={trashOutline} slot="start" color="danger" />
                  <IonLabel>
                    <h3>Annulla importazione</h3>
                    <p style={{ fontSize: 12 }}>Rimuove il documento, la scansione e i lotti caricati in magazzino</p>
                  </IonLabel>
                </IonItem>
              </IonList>
            ) : (
              <IonList inset>
                <IonListHeader><IonLabel>Stato archivio</IonLabel></IonListHeader>
                <IonItem>
                  <IonIcon icon={checkmarkCircleOutline} slot="start" color="medium" />
                  <IonLabel className="ion-text-wrap">
                    <h3>
                      {actionPolicy.can_mutate
                        ? 'Sede originale non verificabile'
                        : 'Documento storico in sola lettura'}
                    </h3>
                    <p style={{ fontSize: 12 }}>
                      {actionPolicy.can_mutate
                        ? 'Il documento resta consultabile, ma le azioni che cambiano lotti o giacenze sono bloccate per evitare attribuzioni a un locale errato.'
                        : 'L’importazione è già stata annullata o sostituita. Le azioni che cambiano documento, lotti o giacenze non sono disponibili.'}
                    </p>
                  </IonLabel>
                </IonItem>
              </IonList>
            )}
          </>
        )}

        <IonAlert
          isOpen={confirmDelete && canMutateDocument && !!cancellationConfirmation}
          backdropDismiss={false}
          onDidDismiss={() => setConfirmDelete(false)}
          header={cancellationConfirmation?.header}
          subHeader={cancellationConfirmation?.subHeader}
          message={cancellationConfirmation?.message}
          inputs={[{
            type: 'checkbox',
            label: 'Ho verificato il documento e voglio annullare il carico',
            value: 'confirmed',
          }]}
          buttons={[
            { text: 'No, torna indietro', role: 'cancel' },
            {
              text: cancellationConfirmation?.confirmText ?? 'Sì, annulla',
              role: 'destructive',
              handler: (values: string[]) => {
                if (!values.includes('confirmed')) {
                  setToast('Spunta la conferma prima di annullare il documento.');
                  return false;
                }
                setConfirmDelete(false);
                void handleDelete();
                return true;
              },
            },
          ]}
        />

        {/* T-195/T-331: correzione riga di carico post-commit (append-only /
            hash-chain compliant): quantità + UdM + lotto + scadenza. La UdM ha
            un vero selettore (es. pz → kg), impossibile con un IonAlert. */}
        <IonModal
          isOpen={!!correctTarget && canMutateDocument}
          onDidDismiss={() => { if (!correcting) setCorrectTarget(null); }}
        >
          <IonHeader>
            <IonToolbar>
              <IonTitle>Correggi carico</IonTitle>
              <IonButtons slot="end"><IonButton onClick={() => { if (!correcting) setCorrectTarget(null); }}>Chiudi</IonButton></IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            {correctTarget && (
              <>
                <p style={{ marginBottom: 4 }}><strong>{correctTarget.food_name}</strong></p>
                <p style={{ color: 'var(--ion-color-medium)', fontSize: '0.85em', marginBottom: 16 }}>
                  Caricato come <b>{correctTarget.qty} {correctTarget.uom}</b>. Correggi quantità, unità, lotto o scadenza:
                  il carico originale resta tracciato, la rettifica è firmata col tuo nome senza alterare l’evento (catena di hash intatta).
                </p>
                <IonItem>
                  <IonLabel position="stacked">Quantità *</IonLabel>
                  <IonInput
                    type="number"
                    inputmode="decimal"
                    value={correctForm.qty}
                    placeholder="es. 10.8"
                    onIonInput={(e) => setCorrectForm((f) => ({ ...f, qty: e.detail.value ?? '' }))}
                  />
                </IonItem>
                <IonItem>
                  <UomSelect
                    label="Unità di magazzino *"
                    value={correctForm.uom}
                    options={[correctTarget.canonical_uom]}
                    allowCustom={false}
                    onChange={(u) => setCorrectForm((f) => ({ ...f, uom: u }))}
                  />
                </IonItem>
                <IonItem>
                  <IonLabel position="stacked">Lotto / N° Fattura</IonLabel>
                  <IonInput
                    value={correctForm.lot}
                    placeholder="(opzionale)"
                    onIonInput={(e) => setCorrectForm((f) => ({ ...f, lot: e.detail.value ?? '' }))}
                  />
                </IonItem>
                <IonItem>
                  <IonLabel position="stacked">Scadenza</IonLabel>
                  <IonInput
                    type="date"
                    value={correctForm.expiry}
                    onIonInput={(e) => setCorrectForm((f) => ({ ...f, expiry: e.detail.value ?? '' }))}
                  />
                </IonItem>
                <IonItem>
                  <IonLabel position="stacked">Motivo *</IonLabel>
                  <IonTextarea
                    autoGrow
                    value={correctForm.reason}
                    placeholder="es. caricato 8 pz ma erano 10,8 kg (UdM sbagliata dalla foto)"
                    onIonInput={(e) => setCorrectForm((f) => ({ ...f, reason: e.detail.value ?? '' }))}
                  />
                </IonItem>
                <IonButton expand="block" style={{ marginTop: 24 }} disabled={correcting} onClick={() => void submitCorrection()}>
                  {correcting ? 'Registrazione…' : 'Conferma correzione'}
                </IonButton>
              </>
            )}
          </IonContent>
        </IonModal>

        <IonToast
          isOpen={!!toast}
          message={toast ?? ''}
          duration={2500}
          onDidDismiss={() => setToast(null)}
        />
        {/* T-189: scelta (ricordata) di come aprire il file originale. */}
        <IonActionSheet
          isOpen={openSheet}
          onDidDismiss={() => setOpenSheet(false)}
          header="Apri il file originale"
          subHeader="Scegli come aprirlo — la scelta viene ricordata"
          buttons={[
            { text: docOpenMode === 'inapp' ? '👁️ Anteprima nell’app  ✓' : '👁️ Anteprima nell’app', handler: () => chooseAndOpen('inapp') },
            { text: docOpenMode === 'external' ? '📤 Apri con un’altra app  ✓' : '📤 Apri con un’altra app', handler: () => chooseAndOpen('external') },
            { text: 'Annulla', role: 'cancel' },
          ]}
        />
      </IonContent>
    </IonPage>
  );
}
